import { useMemo } from 'react';
import { CustomFieldProps } from './types';
import { Checkbox, FormControl, FormControlLabel, FormHelperText, Switch } from '@mui/material';
import classNames from 'classnames/bind';
import DisabledTooltip from '@components/DisabledTooltip';

import styles from './Form.module.css';

const classNamesCtx = classNames.bind(styles);

type CheckControl = 'checkbox' | 'switch';

interface CheckInputProps {
	/** Which MUI boolean control to render. Default: 'checkbox' */
	control?: CheckControl;
	className?: string;
	fieldName?: string;
	label?: string;
	labelPlacement?: 'end' | 'start' | 'top' | 'bottom' | undefined;
	helperText?: string;
	isDirty?: boolean;
	isError?: boolean;
	errorMessage?: string;
	isDisabled?: boolean;
	disabledReason?: string;
	disabledTooltipClass?: string;
	onChange?: (checked: boolean) => void;
	value?: unknown;
}

/** Wrapper around the MUI Checkbox to simplify implementations throughout program */
export function CheckInput(props: CheckInputProps) {
	const fieldClasses = classNamesCtx({
		checkbox: true,
		dirty: props.isDirty,
		error: props.isError,
		[props.className ?? '']: Boolean(props.className)
	});

	const input = (
		<DisabledTooltip
			message={props.disabledReason}
			className={props.disabledTooltipClass}
		>
			<FormControlLabel
				className={fieldClasses}
				label={props.label ? props.label : ''}
				slotProps={{
					typography: {
						className: styles['checkbox-label']
					}
				}}
				labelPlacement={props.labelPlacement ? props.labelPlacement : 'top'}
				name={props.fieldName}
				onChange={(_, checked) => {
					if (props.onChange) {
						props.onChange(checked);
					}
				}}
				control={
					props.control === 'switch' ? (
						<Switch
							className={styles['switch-input']}
							size='small'
							checked={Boolean(props.value)}
							disabled={props.isDisabled}
						/>
					) : (
						<Checkbox
							className={styles['checkbox-input']}
							size='small'
							checked={Boolean(props.value)}
							disabled={props.isDisabled}
						/>
					)
				}
			/>
		</DisabledTooltip>
	);

	if (!props.helperText) {
		return input;
	}

	return (
		<FormControl className={styles['checkbox-control']}>
			{input}
			<FormHelperText className={styles['checkbox-helper-text']}>{props.helperText}</FormHelperText>
		</FormControl>
	);
}

/** HOC to render a Formik Field as a MUI Checkbox or Switch Input */
function asCheckboxInput(control: CheckControl) {
	return ({ field, form, ...props }: CustomFieldProps) => {
		const { value, initialValue, error } = form.getFieldMeta<string | null>(field.name);

		const { dirty } = form;

		const isDirty = useMemo(() => {
			if (dirty) {
				return value !== initialValue;
			}
			return false;
		}, [value, initialValue, dirty]);

		return (
			<CheckInput
				control={control}
				value={value}
				labelPlacement={props.labelPlacement}
				label={props.label}
				helperText={props.helperText}
				fieldName={field.name}
				className={props.className}
				isDisabled={props.disabled || form.isSubmitting}
				disabledReason={props.disabledReason}
				disabledTooltipClass={props.disabledTooltipClass}
				isDirty={isDirty}
				isError={Boolean(error)}
				errorMessage={error}
				onChange={(checked: boolean) => {
					if (props.onChangeExtension) {
						/** Some fields like street turn are not actually a boolean and have custom handlers */
						props.onChangeExtension(checked);
					} else {
						void form.setFieldValue(field.name, checked, true);
					}
				}}
			/>
		);
	};
}

export const FormikCheckboxInput = asCheckboxInput('checkbox');
export const FormikSwitchInput = asCheckboxInput('switch');
